// Зареждането на листа „Прехвърляне" при редакция. Записът идва през router
// state `{transfer}` от документа (без втора заявка); при директно отваряне на
// адреса се чете по id (`GET /inventory/transfers/{id}`). Зареждането НЕ минава
// през `touch()` — пазачът за незапазени промени остава свален.
import { useEffect, useMemo, useRef } from "react";
import dayjs, { type Dayjs } from "dayjs";
import type { Product } from "../../../../api/inventory";
import type { TransferDocument } from "../../../../api/inventoryTransfers";
import { useTransfer } from "../../../../hooks/useInventoryTransfers";
import { formatQtyInput } from "../../components/numbers";
import { clampDecimals } from "../../components/qty";
import type { TransferLineDraft } from "./transferWorksheetShared";

type Params = {
  mode: "new" | "edit";
  transferId: string | undefined;
  transferFromState: TransferDocument | undefined;
  products: Product[];
  actions: {
    setDate: (value: Dayjs) => void;
    setFromWarehouseId: (value: number) => void;
    setToWarehouseId: (value: number) => void;
    setNote: (value: string) => void;
    setLines: (lines: TransferLineDraft[]) => void;
    onLoadError: () => void;
  };
};

export const useTransferLoad = ({
  mode,
  transferId,
  transferFromState,
  products,
  actions,
}: Params) => {
  const needsFetch = mode === "edit" && !transferFromState && transferId != null;
  const fetched = useTransfer(needsFetch ? Number(transferId) : null);
  const editedTransfer: TransferDocument | null =
    mode === "edit" ? (transferFromState ?? fetched.data ?? null) : null;

  // Количествата от записания документ per продукт — за корекцията на
  // наличността в редакция (виж useTransferRowsActions).
  const savedQuantities = useMemo(
    () =>
      editedTransfer
        ? new Map(editedTransfer.lines.map((line) => [line.productId, line.quantity]))
        : null,
    [editedTransfer],
  );
  const savedDate = editedTransfer ? dayjs(editedTransfer.date).format("YYYY-MM-DD") : null;

  const actionsRef = useRef(actions);
  actionsRef.current = actions;

  // Несъществуващ/чужд документ по директен адрес → обратно към операцията
  useEffect(() => {
    if (fetched.isError) actionsRef.current.onLoadError();
  }, [fetched.isError]);

  const loadedRef = useRef(false);
  useEffect(() => {
    if (mode !== "edit" || loadedRef.current || !editedTransfer) return;
    loadedRef.current = true;
    const { setDate, setFromWarehouseId, setToWarehouseId, setNote, setLines } =
      actionsRef.current;
    setDate(dayjs(editedTransfer.date));
    setFromWarehouseId(editedTransfer.fromWarehouseId);
    setToWarehouseId(editedTransfer.toWarehouseId);
    setNote(editedTransfer.note ?? "");
    setLines(
      editedTransfer.lines.map((line) => ({
        productId: line.productId,
        quantity: formatQtyInput(
          line.quantity,
          clampDecimals(products.find((product) => product.id === line.productId)?.decimalPlaces),
        ),
        // партидата се избира наново (както при изписване)
        lotId: null,
      })),
    );
  }, [mode, editedTransfer, products]);

  return { editedTransfer, savedQuantities, savedDate };
};
